import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import usePagination from "../../../../hooks/usePagination";
import { FilterAutocomplete, Table } from "../../../../components/ui";
import { complianceSearchActions } from "../../../../reducers/complianceSearch";
import { useComplianceSearchContext } from "../../../../context";
import { HELIOS_AND_APPIUS_LIVE_TIF_COLUMNS } from "../../constants";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../../api/compliance";
import { appendSearchParamsToQuery, TitleUpdated } from "../../utils";
import { SectionTitle } from "../../styledComponents";

const HeliosAndAppiusLiveSearch: React.FC = () => {
  const pagination = usePagination();
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.complianceSearch.heliosAndAppiusLiveTif);

  const { tabsTablesSearchState, setTabsTablesSearchState } = useComplianceSearchContext();
  const { search } =
    tabsTablesSearchState.fundedAccountQuickSearchTab.heliosAndAppiusLiveSearchTable;

  const { isError, error } = useQuery({
    queryKey: [
      COMPLIANCE_API_URLS.getWatchlistHeliosAppiusLiveTif,
      Object.values(search),
      pagination.page,
      pagination.limit,
    ],
    async queryFn({ signal }) {
      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());
      appendSearchParamsToQuery(search, query);

      dispatch(complianceSearchActions.fetchWatchlistHeliosAndAppiusLiveTifRequest());
      const response = await ComplianceAPI.getWatchlistHeliosAppiusLiveTifRequest(
        query.toString(),
        signal
      );

      pagination.setCount(response.data.count);
      dispatch(complianceSearchActions.fetchWatchlistHeliosAndAppiusLiveTifSuccess(response.data));

      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(complianceSearchActions.fetchWatchlistHeliosAndAppiusLiveTifFailed());
      console.error("Error fetch fetchWatchlistHeliosAndAppiusLiveTifRequest", error);
    }
  }, [isError, error]);

  useEffect(() => {
    pagination.setPage(0);
  }, [search, pagination.limit]);

  return (
    <SectionTitle title="Helios/Appius/Kronos Live TIF">
      <div>
        <FilterAutocomplete
          search={search}
          setSearch={(values) => {
            setTabsTablesSearchState({
              ...tabsTablesSearchState,
              fundedAccountQuickSearchTab: {
                ...tabsTablesSearchState.fundedAccountQuickSearchTab,
                heliosAndAppiusLiveSearchTable: {
                  search: values,
                },
              },
            });
          }}
          filters={{
            Name: "names",
            Email: "email",
            Phone: "phone",
            Address: "address",
            "Entries ID": "compliance_entries_id",
          }}
        />
      </div>

      <Table
        columns={HELIOS_AND_APPIUS_LIVE_TIF_COLUMNS}
        title={<TitleUpdated value={data.updated.initialization_date} />}
        loading={loading}
        pagination={pagination}
        extractKey={(row) => row.id + row.timestamp}
        data={data.rows}
        csv_table_name="Funded account quick search - Helios and Appius Live TIF"
      />
    </SectionTitle>
  );
};

export default HeliosAndAppiusLiveSearch;
